import React from 'react';
import PropTypes from 'prop-types';
import HeaderType01 from './HeaderType01';
import HtmlContainer from './HtmlContainer';
import PropertiesContainer from './PropertiesContainer';
import ContactUsContainer from './ContactUsContainer';
import VirtualShowing01 from './VirtualShowing01';
import FooterType01 from './FooterType01';
import FooterType02 from './FooterType02';
import LeftFooterMenuType01 from './LeftFooterMenuType01';
import Grid01 from './Grid01';
import Grid02 from './Grid02';
import HeaderType02 from './HeaderType02';
import List2ColumnsType01 from './List2ColumnsType01';
import OnePageMenuType01 from './OnePageMenuType01';
import PropertiesContainerType02 from './PropertiesContainerType02';
import BlogPreviewSection from './BlogPreviewSection';
import BlogTabSection from './BlogTabSection';
import ImageTextSplit from './ImageTextSplit';
import HeroContactSection from './HeroContactSection';

const SUB_GENERATORS = {
  'siteSub.header-01': (site, siteSub) => (
    <HeaderType01
      logo={site.logo}
      title={site.title || site.eName}
      subtitle={site.subtitle}
      blogEnabled={site.blogSettings && site.blogSettings.enabled}
      sns={{
        facebook: site.facebook,
        instagram: site.instagram,
        youtube: site.youtube,
      }}
      snsLinkBackgroundColor={site.snsLinkBackgroundColor}
      snsLinkTextColor={site.snsLinkTextColor}
    />
  ),
  'siteSub.header-02': (site, siteSub) => (
    <HeaderType02
      id={siteSub.id}
      options={siteSub.objects}
      blogEnabled={site.blogSettings && site.blogSettings.enabled}
      sns={{
        facebook: site.facebook,
        instagram: site.instagram,
        youtube: site.youtube,
        blog: site.blog,
      }}
      brokerage={site.brokerage}
    />
  ),
  'siteSub.html': (site, siteSub) => (
    <HtmlContainer
      id={siteSub.id}
      html={siteSub.html}
      style={siteSub.style}
      script={siteSub.script}
    />
  ),
  'siteSub.properties-01': (site, siteSub) => (
    <PropertiesContainer options={siteSub.objects} hasRibbon />
  ),
  'siteSub.properties-02': (site, siteSub) => (
    <PropertiesContainerType02 id={siteSub.id} options={siteSub.objects} />
  ),
  'siteSub.properties-03': (site, siteSub) => (
    <PropertiesContainerType02 id={siteSub.id} objects={siteSub.objects} />
  ),
  'siteSub.contactus': (site, siteSub) => (
    <ContactUsContainer
      id={siteSub.id}
      options={siteSub.objects}
      phone={site.phone}
      googleFormLink={site.googleFormLink}
      formEndpoint={site.formEndpoint}
      portrait={siteSub.objects.portrait}
      backgroundImage={siteSub.objects.backgroundImage}
      padding={siteSub.objects.padding}
      margin={siteSub.objects.margin}
    />
  ),
  'siteSub.virtualshowing-01': (site, siteSub) => (
    <VirtualShowing01
      phone={site.phone}
      options={siteSub.objects}
      propertyType="1"
      formEndpoint={site.formEndpoint}
    />
  ),
  'siteSub.virtualshowing-02': (site, siteSub) => (
    <VirtualShowing01
      phone={site.phone}
      options={siteSub.objects}
      propertyType="2"
      formEndpoint={site.formEndpoint}
    />
  ),
  'siteSub.footer-01': () => <FooterType01 />,
  'siteSub.footer-02': () => <FooterType02 />,
  'siteSub.left-fixed-menu-01': (site) => (
    <LeftFooterMenuType01
      containerClass="d-none d-sm-none d-md-block"
      phone={site.phone}
      phoneAvailable={site.phoneAvailable}
      email={site.email}
      kakao={site.kakao}
      blog={site.blog}
      googleFormLink={site.googleFormLink}
    />
  ),
  'siteSub.grid-01': (site, siteSub) => (
    <Grid01 containerClass="container" siteSub={siteSub} />
  ),
  'siteSub.grid-02': (site, siteSub) => (
    <Grid02 containerClass="container" siteSub={siteSub} />
  ),
  'siteSub.list-2column-01': (site, siteSub) => (
    <List2ColumnsType01 containerClass="container" siteSub={siteSub} />
  ),
  'siteSub.onepage-menu-01': (site, siteSub) => (
    <OnePageMenuType01 id={siteSub.id} options={siteSub.objects} />
  ),
  'siteSub.blog-preview': (site, siteSub) => (
    <BlogPreviewSection siteId={site.id} id={siteSub.id} options={siteSub.objects} />
  ),
  'siteSub.blog-tab': (site, siteSub) => (
    <BlogTabSection siteId={site.id} id={siteSub.id} options={siteSub.objects} />
  ),
  'siteSub.image-text-split': (site, siteSub) => (
    <ImageTextSplit
      id={siteSub.id}
      options={siteSub.objects}
    />
  ),
  'siteSub.hero-contact': (site, siteSub) => (
    <HeroContactSection
      id={siteSub.id}
      options={siteSub.objects}
      phone={site.phone}
      googleFormLink={site.googleFormLink}
      backgroundImage={siteSub.objects?.backgroundImage}
    />
  ),
};

const SiteSubGenerator = ({ site, siteSub }) => {
  // Try exact match first, then with 'siteSub.' prefix for backward compatibility
  const generator = SUB_GENERATORS[siteSub.type] || SUB_GENERATORS[`siteSub.${siteSub.type}`];
  return generator ? generator(site, siteSub) : <div></div>;
};

SiteSubGenerator.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createdAt: PropTypes.string,
    hasBackground: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    formEndpoint: PropTypes.string,
    googleFormLink: PropTypes.string,
    name: PropTypes.string,
    eName: PropTypes.string,
    phone: PropTypes.string,
    phoneAvailable: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    address: PropTypes.string,
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    youtube: PropTypes.string,
    kakao: PropTypes.string,
    blog: PropTypes.string,
  }),
  siteSub: PropTypes.shape({
    type: PropTypes.string,
    objects: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    html: PropTypes.string,
    style: PropTypes.string,
    script: PropTypes.string,
    order: PropTypes.number,
  }),
};

export default SiteSubGenerator;
